import React, { ReactNode, useState } from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa'; // Import icons

type ColumnType = 'text' | 'number' | 'date' | 'array' | 'component' | 'password';

interface Column {
  key: string;
  title: string;
  type: ColumnType;
  sortable?: boolean;
}

interface Row {
  [key: string]: string | number | Date | any[] | ReactNode;
}

interface TableProps {
  columns: Column[];
  data: Row[];
  loading?: boolean; // Add loading prop
  onCellClick?: (row: Row, column: Column, cellValue: any) => void;
}

type SortOrder = 'asc' | 'desc';

const Table: React.FC<TableProps> = ({ columns, data, loading, onCellClick }) => {
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: SortOrder } | null>(null);
  const [passwordVisibility, setPasswordVisibility] = useState<{ [key: number]: boolean }>({});

  const serialNumberColumn: Column = { key: 'serial', title: 'S.No', type: 'number', sortable: false };
  const extendedColumns = [serialNumberColumn, ...columns];

  const handleClick = (row: Row, column: Column, cellValue: any) => {
    if (onCellClick) {
      onCellClick(row, column, cellValue);
    }
  };

  const handleSort = (key: string) => {
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      setSortConfig({ key, direction: 'desc' });
    } else {
      setSortConfig({ key, direction: 'asc' });
    }
  };

  const togglePasswordVisibility = (rowIndex: number) => {
    setPasswordVisibility((prevState) => ({
      ...prevState,
      [rowIndex]: !prevState[rowIndex],
    }));
  };

  const sortedData = React.useMemo(() => {
    if (sortConfig !== null) {
      return [...data].sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        if (aValue instanceof Date && bValue instanceof Date) {
          return sortConfig.direction === 'asc'
            ? aValue.getTime() - bValue.getTime()
            : bValue.getTime() - aValue.getTime();
        }

        return sortConfig.direction === 'asc'
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      });
    }
    return data;
  }, [data, sortConfig]);

  const renderCellValue = (column: Column, value: any, rowIndex?: number) => {
    if (column.key === 'serial') {
      return rowIndex !== undefined ? rowIndex + 1 : '';
    }

    switch (column.type) {
      case 'number':
        return typeof value === 'number' ? value.toFixed(2) : value;
      case 'date':
        return value instanceof Date ? value.toLocaleDateString() : value;
      case 'array':
        return Array.isArray(value) ? value.join(', ') : value;
      case 'password':
        return (
          <div className="flex items-center justify-center">
            <span>
              {passwordVisibility[rowIndex || 0] ? value : '••••••••'}
            </span>
            <button
              onClick={() => togglePasswordVisibility(rowIndex || 0)}
              className="ml-2 focus:outline-none"
            >
              {passwordVisibility[rowIndex || 0] ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
        );
      case 'component':
        return value;
      default:
        return value;
    }
  };

  const renderSkeletonRow = () => {
    return (
      <tr className="animate-pulse bg-gray-100">
        {extendedColumns.map((column, index) => (
          <td key={index} className="p-2 border-b border-gray-300 text-center">
            <div className="h-4 bg-gray-300 rounded"></div>
          </td>
        ))}
      </tr>
    );
  };

  return (
    <div className="  p-4">
      <table className="w-full rounded-md  overflow-x-auto  overflow-y-auto scrollbar shadow-lg  p-2 ">
        <thead className="bg-primary sticky top-0 text-white">
          <tr>
            {extendedColumns.map((column, idx) => (
              <th
                key={idx}
                onClick={() => column.sortable !== false && handleSort(column.key)}
                className={`p-2 border-b border-gray-300 text-center ${
                  column.key === 'serial' || column.sortable === false ? '' : 'cursor-pointer'
                } ${column.key === 'serial' ? 'bg-primary/10' : ''}`}
              >
                {column.title}{' '}
                {column.sortable !== false &&
                  sortConfig?.key === column.key &&
                  (sortConfig.direction === 'asc' ? '▲' : '▼')}
              </th>
            ))}
          </tr>
        </thead>
          <tbody className="w-full">
          {loading
            ? Array.from({ length: 8 }).map((_, idx) => renderSkeletonRow()):data.length>0? 
            sortedData.map((row, rowIndex) => (
                <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-hoverLight/40' : 'bg-white'}>
                  {extendedColumns.map((column, i) => (
                    <td
                      key={i}
                      onClick={() => handleClick(row, column, row[column.key])}
                      className={`p-2 border-b border-gray-300 text-center ${
                        column.key === 'serial' ? 'bg-white' : ''
                      } text-sm md:text-base`}
                    >
                      {renderCellValue(column, row[column.key], rowIndex)}
                    </td>
                  ))}
                </tr>
              )):<p className='text-center w-full flex items-center '>No Data</p>}
        </tbody>
      
      </table>
    </div>
  );
};

export default Table;
